import { useEffect, useState } from "react";
import { getPricingSettings, updatePricingSettings, getPricingHistory } from "../../api/client";
import AdminNav from "../../components/AdminNav";
import { PACKAGE_TYPE_LABELS } from "../../utils/packageLabels";

const VEHICLE_LABELS = { bicycle: "Bicycle", bike: "Bike / Motorcycle", van: "Van / Bus", truck: "Truck / Heavy Cargo" };
const RA_METHOD_LABELS = { pin: "4-Digit PIN", signature: "Digital Signature", photo: "Package Photo", id_photo: "Receiver ID Photo" };

export default function PricingSettings() {
  const [settings, setSettings] = useState(null);
  const [form, setForm] = useState({});
  const [history, setHistory] = useState([]);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [newCity, setNewCity] = useState("");

  const load = async () => {
    try {
      const [{ data: settingsData }, { data: historyData }] = await Promise.all([
        getPricingSettings(),
        getPricingHistory()
      ]);
      setSettings(settingsData);
      setForm(settingsData);
      setHistory(historyData);
    } catch (err) {
      setError(err.response?.data?.error || "Could not load pricing settings");
    }
  };

  useEffect(() => { load(); }, []);

  const handleChange = (key, value) => {
    setSaved(false);
    setForm((prev) => ({ ...prev, [key]: Number(value) }));
  };

  const handleStringChange = (key, value) => {
    setSaved(false);
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleNestedChange = (group, key, value) => {
    setSaved(false);
    setForm((prev) => ({
      ...prev,
      [group]: { ...(prev[group] || {}), [key]: Number(value) }
    }));
  };

  const handleToggle = (key) => {
    setSaved(false);
    setForm((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const handleChannelToggle = (channel) => {
    setSaved(false);
    setForm((prev) => ({
      ...prev,
      paymentChannels: {
        paystack: prev.paymentChannels?.paystack ?? true,
        flutterwave: prev.paymentChannels?.flutterwave ?? true,
        monnify: prev.paymentChannels?.monnify ?? true,
        [channel]: !(prev.paymentChannels?.[channel] ?? true)
      }
    }));
  };

  const handleAddCity = () => {
    if (!newCity.trim()) return;
    const cities = form.launchedCities || [];
    if (cities.includes(newCity.trim())) return;
    setSaved(false);
    setForm((prev) => ({ ...prev, launchedCities: [...cities, newCity.trim()] }));
    setNewCity("");
  };

  const handleRemoveCity = (city) => {
    setSaved(false);
    setForm((prev) => ({
      ...prev,
      launchedCities: (prev.launchedCities || []).filter((c) => c !== city)
    }));
  };

  const handleSave = async () => {
    setError("");
    setSaving(true);
    try {
      const { data } = await updatePricingSettings(form);
      setSettings(data);
      setForm(data);
      setSaved(true);
      const { data: historyData } = await getPricingHistory();
      setHistory(historyData);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      setError(err.response?.data?.error || "Could not save pricing settings");
    } finally {
      setSaving(false);
    }
  };

  if (!settings) {
    return (
      <div className="admin-app">
        <AdminNav />
        <div className="page wide page-with-nav">
          {error ? <p className="error">{error}</p> : <div className="empty-state"><p className="empty-state-text waiting-dots">Loading settings</p></div>}
        </div>
      </div>
    );
  }

  const commission = Number(form.commissionPercent ?? 20);
  const dispatcherShare = 100 - commission;

  return (
    <div className="admin-app">
      <AdminNav />
      <div className="page wide page-with-nav">
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: "16px", marginBottom: "20px" }}>
          <div>
            <h1 style={{ margin: "0 0 4px" }}>💰 Pricing & Marketplace Settings</h1>
            <p className="hint" style={{ margin: 0 }}>Configure base rates, dispatcher ratios, distance tiers, incentives, and operational rules.</p>
          </div>
          <button
            disabled={saving}
            onClick={handleSave}
            style={{ width: "auto", padding: "10px 24px", fontWeight: 700 }}
          >
            {saving ? "Saving Changes..." : "💾 Save Changes"}
          </button>
        </div>

        {error && <p className="error" style={{ marginBottom: "16px" }}>{error}</p>}
        {saved && (
          <div style={{ marginBottom: "16px", padding: "12px 16px", background: "rgba(16, 185, 129, 0.15)", border: "1px solid var(--success, #10b981)", borderRadius: "8px", color: "var(--success, #10b981)", display: "flex", alignItems: "center", gap: "8px" }}>
            <span>✅</span>
            <strong>Pricing settings saved and live across all platform quotes!</strong>
          </div>
        )}

        <div style={{ display: "flex", flexDirection: "column", gap: "24px" }}>

          {/* CARD 1: PLATFORM REVENUE RATIO & SPLIT */}
          <div className="delivery-card" style={{ background: "var(--card-bg, #1a2234)", border: "1px solid var(--border-color, #2d3748)", borderRadius: "12px", padding: "20px" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "8px", marginBottom: "12px" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                <span style={{ fontSize: "24px" }}>🤝</span>
                <div>
                  <h3 style={{ margin: 0, fontSize: "16px" }}>Platform Revenue Ratio & Commission Split</h3>
                  <p className="hint" style={{ margin: 0 }}>Current split: Dispatcher {dispatcherShare}% / Platform {commission}%</p>
                </div>
              </div>
              <div style={{ display: "flex", gap: "8px" }}>
                <button
                  type="button"
                  className={commission === 20 ? "" : "secondary"}
                  style={{ width: "auto", padding: "6px 12px", fontSize: "13px", margin: 0 }}
                  onClick={() => handleChange("commissionPercent", 20)}
                >
                  80 / 20 Split (Standard)
                </button>
                <button
                  type="button"
                  className={commission === 22 ? "" : "secondary"}
                  style={{ width: "auto", padding: "6px 12px", fontSize: "13px", margin: 0 }}
                  onClick={() => handleChange("commissionPercent", 22)}
                >
                  78 / 22 Split
                </button>
              </div>
            </div>

            <div style={{ marginBottom: "16px" }}>
              <div style={{ height: "12px", width: "100%", borderRadius: "6px", overflow: "hidden", display: "flex", background: "#374151" }}>
                <div style={{ width: `${dispatcherShare}%`, background: "#3b82f6", transition: "width 0.3s" }} title={`Dispatcher: ${dispatcherShare}%`} />
                <div style={{ width: `${commission}%`, background: "#10b981", transition: "width 0.3s" }} title={`Platform Cut: ${commission}%`} />
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", fontSize: "12px", marginTop: "6px", color: "var(--text-muted)" }}>
                <span>🛵 Dispatcher Take-Home: <strong style={{ color: "#60a5fa" }}>{dispatcherShare}%</strong></span>
                <span>🏢 Platform Commission: <strong style={{ color: "#34d399" }}>{commission}%</strong></span>
              </div>
            </div>

            <div style={{ maxWidth: "260px" }}>
              <label style={{ fontSize: "12px", color: "var(--text-muted)", display: "block", marginBottom: "4px" }}>Custom Commission Rate (%)</label>
              <input
                type="number"
                min="0"
                max="50"
                step="0.5"
                value={form.commissionPercent ?? 20}
                onChange={(e) => handleChange("commissionPercent", e.target.value)}
                style={{ margin: 0 }}
              />
            </div>
          </div>

          {/* CARD 2: INTER / INTRA-STATE PRICING */}
          <div className="delivery-card" style={{ background: "var(--card-bg, #1a2234)", border: "1px solid var(--border-color, #2d3748)", borderRadius: "12px", padding: "20px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "12px" }}>
              <span style={{ fontSize: "24px" }}>🛣️</span>
              <div>
                <h3 style={{ margin: 0, fontSize: "16px" }}>Inter / Intra-State Delivery Pricing</h3>
                <p className="hint" style={{ margin: 0 }}>Orders below threshold are charged standard rate; distance beyond threshold applies interstate rate.</p>
              </div>
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px" }}>
              <div className="form-field" style={{ margin: 0 }}>
                <label style={{ fontSize: "12px", color: "var(--text-muted)" }}>Distance Threshold (KM)</label>
                <input
                  type="number"
                  min="5"
                  step="1"
                  value={form.interstateThresholdKm ?? 25}
                  onChange={(e) => handleChange("interstateThresholdKm", e.target.value)}
                />
                <p className="hint">Standard price applies up to this distance (Default: 25 km).</p>
              </div>
              <div className="form-field" style={{ margin: 0 }}>
                <label style={{ fontSize: "12px", color: "var(--text-muted)" }}>Interstate Excess Rate Per KM (₦)</label>
                <input
                  type="number"
                  min="0"
                  step="10"
                  value={form.interstateRatePerKm ?? 100}
                  onChange={(e) => handleChange("interstateRatePerKm", e.target.value)}
                />
                <p className="hint">Rate charged per kilometer for excess distance beyond threshold.</p>
              </div>
            </div>
          </div>

          {/* CARD 3: BASE DELIVERY & MILEAGE */}
          <div className="delivery-card" style={{ background: "var(--card-bg, #1a2234)", border: "1px solid var(--border-color, #2d3748)", borderRadius: "12px", padding: "20px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "16px" }}>
              <span style={{ fontSize: "24px" }}>🚗</span>
              <div>
                <h3 style={{ margin: 0, fontSize: "16px" }}>Base Delivery & Mileage Rates</h3>
                <p className="hint" style={{ margin: 0 }}>Core pricing components used to calculate delivery quotes.</p>
              </div>
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px" }}>
              <div className="form-field" style={{ margin: 0 }}>
                <label style={{ fontSize: "12px", color: "var(--text-muted)" }}>Base Fare (₦)</label>
                <input
                  type="number"
                  value={form.baseFare ?? 600}
                  onChange={(e) => handleChange("baseFare", e.target.value)}
                />
                <p className="hint">Flat starting charge for every delivery order.</p>
              </div>
              <div className="form-field" style={{ margin: 0 }}>
                <label style={{ fontSize: "12px", color: "var(--text-muted)" }}>Rate Per KM (₦)</label>
                <input
                  type="number"
                  value={form.ratePerKm ?? 100}
                  onChange={(e) => handleChange("ratePerKm", e.target.value)}
                />
                <p className="hint">Standard cost per kilometer within threshold distance.</p>
              </div>
              <div className="form-field" style={{ margin: 0 }}>
                <label style={{ fontSize: "12px", color: "var(--text-muted)" }}>Minimum Fare (₦)</label>
                <input
                  type="number"
                  value={form.minimumFare ?? 700}
                  onChange={(e) => handleChange("minimumFare", e.target.value)}
                />
                <p className="hint">Lowest possible charge regardless of discounts or short distance.</p>
              </div>
              <div className="form-field" style={{ margin: 0 }}>
                <label style={{ fontSize: "12px", color: "var(--text-muted)" }}>Wallet Payment Discount (%)</label>
                <input
                  type="number"
                  value={form.walletDiscountPercent ?? 5}
                  onChange={(e) => handleChange("walletDiscountPercent", e.target.value)}
                />
                <p className="hint">Incentive discount when customers pay via user wallet balance.</p>
              </div>
            </div>
          </div>

          {/* PAYMENT CHANNELS MASTER CONTROL */}
          <div className="delivery-card" style={{ background: "var(--card-bg, #1a2234)", border: "1px solid var(--border-color, #2d3748)", borderRadius: "12px", padding: "20px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "16px" }}>
              <span style={{ fontSize: "24px" }}>💳</span>
              <div>
                <h3 style={{ margin: 0, fontSize: "16px" }}>Payment Channels Master Control</h3>
                <p className="hint" style={{ margin: 0 }}>Master toggle for payment gateways & collection channels on the platform.</p>
              </div>
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: "16px" }}>
              {/* Paystack */}
              <div style={{ background: "rgba(255,255,255,0.02)", padding: "16px", borderRadius: "10px", border: "1px solid var(--border)" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "10px" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                    <span style={{ fontSize: "18px" }}>⚡</span>
                    <strong>Paystack</strong>
                  </div>
                  <button
                    type="button"
                    className={`toggle-switch ${(form.paymentChannels?.paystack ?? true) ? "on" : ""}`}
                    onClick={() => handleChannelToggle("paystack")}
                  />
                </div>
                <p className="hint" style={{ fontSize: "12px", margin: "0 0 8px" }}>Cards, USSD, Bank Transfer, Dedicated NUBAN</p>
                <span className={`status-badge ${(form.paymentChannels?.paystack ?? true) ? "info" : "danger"}`} style={{ fontSize: "11px" }}>
                  {(form.paymentChannels?.paystack ?? true) ? "ACTIVE" : "DISABLED"}
                </span>
              </div>

              {/* Flutterwave */}
              <div style={{ background: "rgba(255,255,255,0.02)", padding: "16px", borderRadius: "10px", border: "1px solid var(--border)" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "10px" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                    <span style={{ fontSize: "18px" }}>🌊</span>
                    <strong>Flutterwave</strong>
                  </div>
                  <button
                    type="button"
                    className={`toggle-switch ${(form.paymentChannels?.flutterwave ?? true) ? "on" : ""}`}
                    onClick={() => handleChannelToggle("flutterwave")}
                  />
                </div>
                <p className="hint" style={{ fontSize: "12px", margin: "0 0 8px" }}>Cards, Mobile Money, Bank Account, Virtual NUBAN</p>
                <span className={`status-badge ${(form.paymentChannels?.flutterwave ?? true) ? "info" : "danger"}`} style={{ fontSize: "11px" }}>
                  {(form.paymentChannels?.flutterwave ?? true) ? "ACTIVE" : "DISABLED"}
                </span>
              </div>

              {/* Monnify */}
              <div style={{ background: "rgba(255,255,255,0.02)", padding: "16px", borderRadius: "10px", border: "1px solid var(--border)" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "10px" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                    <span style={{ fontSize: "18px" }}>🏦</span>
                    <strong>Monnify</strong>
                  </div>
                  <button
                    type="button"
                    className={`toggle-switch ${(form.paymentChannels?.monnify ?? true) ? "on" : ""}`}
                    onClick={() => handleChannelToggle("monnify")}
                  />
                </div>
                <p className="hint" style={{ fontSize: "12px", margin: "0 0 8px" }}>Dynamic NUBAN Transfer, Cards, USSD</p>
                <span className={`status-badge ${(form.paymentChannels?.monnify ?? true) ? "info" : "danger"}`} style={{ fontSize: "11px" }}>
                  {(form.paymentChannels?.monnify ?? true) ? "ACTIVE" : "DISABLED"}
                </span>
              </div>
            </div>
          </div>

          {/* CARD 4: WELCOME BONUS & INCENTIVES */}
          <div className="delivery-card" style={{ background: "var(--card-bg, #1a2234)", border: "1px solid var(--border-color, #2d3748)", borderRadius: "12px", padding: "20px" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                <span style={{ fontSize: "24px" }}>🎁</span>
                <div>
                  <h3 style={{ margin: 0, fontSize: "16px" }}>Welcome Bonus & Referral Incentives</h3>
                  <p className="hint" style={{ margin: 0 }}>Auto-credits wallet on first completed delivery order up to cap.</p>
                </div>
              </div>
              <button
                type="button"
                className={`toggle-switch ${form.welcomeBonusEnabled ? "on" : ""}`}
                onClick={() => handleToggle("welcomeBonusEnabled")}
              />
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "16px" }}>
              <div className="form-field" style={{ margin: 0 }}>
                <label style={{ fontSize: "12px", color: "var(--text-muted)" }}>Welcome Bonus (%)</label>
                <input
                  type="number"
                  disabled={!form.welcomeBonusEnabled}
                  value={form.welcomeBonusPercent ?? 10}
                  onChange={(e) => handleChange("welcomeBonusPercent", e.target.value)}
                />
                <p className="hint">Percentage of first order total credited to wallet.</p>
              </div>
              <div className="form-field" style={{ margin: 0 }}>
                <label style={{ fontSize: "12px", color: "var(--text-muted)" }}>Max Welcome Bonus (₦)</label>
                <input
                  type="number"
                  disabled={!form.welcomeBonusEnabled}
                  value={form.welcomeBonusMaxAmount ?? 1000}
                  onChange={(e) => handleChange("welcomeBonusMaxAmount", e.target.value)}
                />
                <p className="hint">Ceiling cap on the welcome bonus (Default: ₦1,000).</p>
              </div>
              <div className="form-field" style={{ margin: 0 }}>
                <label style={{ fontSize: "12px", color: "var(--text-muted)" }}>Dispatcher Referral Voucher (₦)</label>
                <input
                  type="number"
                  value={form.referralBonusAmount ?? 1000}
                  onChange={(e) => handleChange("referralBonusAmount", e.target.value)}
                />
                <p className="hint">Bonus paid when a referred dispatcher completes onboarding.</p>
              </div>
            </div>
          </div>

          {/* CARD 5: SEASONAL CASHBACK & PROMOTIONS */}
          <div className="delivery-card" style={{ background: "var(--card-bg, #1a2234)", border: "1px solid var(--border-color, #2d3748)", borderRadius: "12px", padding: "20px" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                <span style={{ fontSize: "24px" }}>🏷️</span>
                <div>
                  <h3 style={{ margin: 0, fontSize: "16px" }}>Seasonal Promotions & Cashback</h3>
                  <p className="hint" style={{ margin: 0 }}>Toggle active seasonal campaigns and promo code vouchers.</p>
                </div>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <span style={{ fontSize: "12px", color: form.seasonalCashbackEnabled ? "var(--success)" : "var(--text-muted)" }}>
                  {form.seasonalCashbackEnabled ? "Cashback Active" : "Cashback Off"}
                </span>
                <button
                  type="button"
                  className={`toggle-switch ${form.seasonalCashbackEnabled ? "on" : ""}`}
                  onClick={() => handleToggle("seasonalCashbackEnabled")}
                />
              </div>
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: "16px" }}>
              <div className="form-field" style={{ margin: 0 }}>
                <label style={{ fontSize: "12px", color: "var(--text-muted)" }}>Seasonal Promo Code</label>
                <input
                  type="text"
                  placeholder="e.g. SEASONAL"
                  value={form.seasonalPromoCode ?? "SEASONAL"}
                  onChange={(e) => handleStringChange("seasonalPromoCode", e.target.value.toUpperCase())}
                />
                <p className="hint">Code customers enter at checkout.</p>
              </div>
              <div className="form-field" style={{ margin: 0 }}>
                <label style={{ fontSize: "12px", color: "var(--text-muted)" }}>Promo Discount (%)</label>
                <input
                  type="number"
                  value={form.seasonalPromoDiscountPercent ?? 10}
                  onChange={(e) => handleChange("seasonalPromoDiscountPercent", e.target.value)}
                />
                <p className="hint">Instant discount applied on quote.</p>
              </div>
              <div className="form-field" style={{ margin: 0 }}>
                <label style={{ fontSize: "12px", color: "var(--text-muted)" }}>Cashback Rate (%)</label>
                <input
                  type="number"
                  disabled={!form.seasonalCashbackEnabled}
                  value={form.seasonalCashbackPercent ?? 5}
                  onChange={(e) => handleChange("seasonalCashbackPercent", e.target.value)}
                />
                <p className="hint">Credited back to wallet upon delivery.</p>
              </div>
              <div className="form-field" style={{ margin: 0 }}>
                <label style={{ fontSize: "12px", color: "var(--text-muted)" }}>Cashback Max Cap (₦)</label>
                <input
                  type="number"
                  disabled={!form.seasonalCashbackEnabled}
                  value={form.seasonalCashbackMaxAmount ?? 1000}
                  onChange={(e) => handleChange("seasonalCashbackMaxAmount", e.target.value)}
                />
                <p className="hint">Maximum cashback per completed order.</p>
              </div>
            </div>
          </div>

          {/* CARD 6: PACKAGE PROTECTION & INSURANCE */}
          <div className="delivery-card" style={{ background: "var(--card-bg, #1a2234)", border: "1px solid var(--border-color, #2d3748)", borderRadius: "12px", padding: "20px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "16px" }}>
              <span style={{ fontSize: "24px" }}>🛡️</span>
              <div>
                <h3 style={{ margin: 0, fontSize: "16px" }}>Package Protection & Insurance</h3>
                <p className="hint" style={{ margin: 0 }}>Charged based on sender-declared package value.</p>
              </div>
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px" }}>
              <div className="form-field" style={{ margin: 0 }}>
                <label style={{ fontSize: "12px", color: "var(--text-muted)" }}>Insurance Rate (%)</label>
                <input
                  type="number"
                  step="0.1"
                  value={form.insuranceRatePercent ?? 1.5}
                  onChange={(e) => handleChange("insuranceRatePercent", e.target.value)}
                />
                <p className="hint">Percentage of declared goods value charged for coverage.</p>
              </div>
              <div className="form-field" style={{ margin: 0 }}>
                <label style={{ fontSize: "12px", color: "var(--text-muted)" }}>Min Insurance Fee (₦)</label>
                <input
                  type="number"
                  value={form.minInsuranceFee ?? 200}
                  onChange={(e) => handleChange("minInsuranceFee", e.target.value)}
                />
                <p className="hint">Minimum fee charged when protection is selected.</p>
              </div>
            </div>
          </div>

          {/* CARD 7: MEMBERSHIP SUBSCRIPTIONS */}
          <div className="delivery-card" style={{ background: "var(--card-bg, #1a2234)", border: "1px solid var(--border-color, #2d3748)", borderRadius: "12px", padding: "20px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "16px" }}>
              <span style={{ fontSize: "24px" }}>💳</span>
              <div>
                <h3 style={{ margin: 0, fontSize: "16px" }}>Membership Subscriptions (Plus & Saver)</h3>
                <p className="hint" style={{ margin: 0 }}>Monthly subscription plan pricing and tier benefits.</p>
              </div>
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px", marginBottom: "16px" }}>
              <div className="form-field" style={{ margin: 0 }}>
                <label style={{ fontSize: "12px", color: "var(--text-muted)" }}>FASTSEND Plus Price (₦ / month)</label>
                <input
                  type="number"
                  value={form.subscriptionMonthlyPrice ?? 2500}
                  onChange={(e) => handleChange("subscriptionMonthlyPrice", e.target.value)}
                />
              </div>
              <div className="form-field" style={{ margin: 0 }}>
                <label style={{ fontSize: "12px", color: "var(--text-muted)" }}>Plus Flat Discount (%)</label>
                <input
                  type="number"
                  value={form.plusDiscountPercent ?? 20}
                  onChange={(e) => handleChange("plusDiscountPercent", e.target.value)}
                />
              </div>
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "16px" }}>
              <div className="form-field" style={{ margin: 0 }}>
                <label style={{ fontSize: "12px", color: "var(--text-muted)" }}>Saver Plan Price (₦ / month)</label>
                <input
                  type="number"
                  value={form.saverMonthlyPrice ?? 5000}
                  onChange={(e) => handleChange("saverMonthlyPrice", e.target.value)}
                />
              </div>
              <div className="form-field" style={{ margin: 0 }}>
                <label style={{ fontSize: "12px", color: "var(--text-muted)" }}>Saver Delivery Cap</label>
                <input
                  type="number"
                  value={form.saverDeliveryCap ?? 20}
                  onChange={(e) => handleChange("saverDeliveryCap", e.target.value)}
                />
                <p className="hint">Monthly deliveries covered under Saver plan.</p>
              </div>
              <div className="form-field" style={{ margin: 0 }}>
                <label style={{ fontSize: "12px", color: "var(--text-muted)" }}>Saver Max Discount (%)</label>
                <input
                  type="number"
                  value={form.saverMaxDiscountPercent ?? 18}
                  onChange={(e) => handleChange("saverMaxDiscountPercent", e.target.value)}
                />
              </div>
            </div>
          </div>

          {/* CARD 8: SURGE & OPERATIONAL SWITCHES */}
          <div className="delivery-card" style={{ background: "var(--card-bg, #1a2234)", border: "1px solid var(--border-color, #2d3748)", borderRadius: "12px", padding: "20px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "16px" }}>
              <span style={{ fontSize: "24px" }}>⚡</span>
              <div>
                <h3 style={{ margin: 0, fontSize: "16px" }}>Surge & Operational Controls</h3>
                <p className="hint" style={{ margin: 0 }}>Live operational toggles and multipliers.</p>
              </div>
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "12px", background: "rgba(255,255,255,0.02)", borderRadius: "8px" }}>
                <div>
                  <strong>Surge Pricing Mode</strong>
                  <span className="hint" style={{ display: "block" }}>
                    {form.surgeEnabled ? "⚡ Surge multiplier active on new orders" : "Standard pricing active"}
                  </span>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                  <input
                    type="number"
                    step="0.1"
                    style={{ width: "90px", margin: 0 }}
                    value={form.surgeMultiplier ?? 1.3}
                    onChange={(e) => handleChange("surgeMultiplier", e.target.value)}
                  />
                  <button
                    type="button"
                    className={`toggle-switch ${form.surgeEnabled ? "on" : ""}`}
                    onClick={() => handleToggle("surgeEnabled")}
                  />
                </div>
              </div>

              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "12px", background: "rgba(255,255,255,0.02)", borderRadius: "8px" }}>
                <div>
                  <strong>Collect Payment on Behalf of Sender (COD / Cash Inflow)</strong>
                  <span className="hint" style={{ display: "block" }}>
                    {form.collectOnBehalfEnabled ? "Live — available on orders" : "Disabled pending gateway activation"}
                  </span>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                  <input
                    type="number"
                    style={{ width: "90px", margin: 0 }}
                    value={form.collectOnBehalfFee ?? 100}
                    onChange={(e) => handleChange("collectOnBehalfFee", e.target.value)}
                  />
                  <button
                    type="button"
                    className={`toggle-switch ${form.collectOnBehalfEnabled ? "on" : ""}`}
                    onClick={() => handleToggle("collectOnBehalfEnabled")}
                  />
                </div>
              </div>

              <div style={{ maxWidth: "300px" }}>
                <label style={{ fontSize: "12px", color: "var(--text-muted)" }}>Truck Auto-Quote Multiplier</label>
                <input
                  type="number"
                  step="0.05"
                  value={form.truckAutoQuoteMultiplier ?? 1.75}
                  onChange={(e) => handleChange("truckAutoQuoteMultiplier", e.target.value)}
                />
                <p className="hint">Multiplier applied to raw estimate if truck price isn't manually set within 10 min.</p>
              </div>
            </div>
          </div>

          {/* CARD 9: PACKAGE WEIGHT-CLASS MODIFIERS */}
          <div className="delivery-card" style={{ background: "var(--card-bg, #1a2234)", border: "1px solid var(--border-color, #2d3748)", borderRadius: "12px", padding: "20px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "16px" }}>
              <span style={{ fontSize: "24px" }}>📦</span>
              <div>
                <h3 style={{ margin: 0, fontSize: "16px" }}>Package Weight-Class Modifiers</h3>
                <p className="hint" style={{ margin: 0 }}>Multipliers applied relative to baseline weight for each vehicle tier.</p>
              </div>
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "16px" }}>
              {Object.keys(PACKAGE_TYPE_LABELS).map((key) => (
                <div key={key} className="form-field" style={{ margin: 0 }}>
                  <label style={{ fontSize: "12px", color: "var(--text-muted)" }}>{PACKAGE_TYPE_LABELS[key]}</label>
                  <input
                    type="number"
                    step="0.1"
                    value={form.packageModifiers?.[key] ?? ""}
                    onChange={(e) => handleNestedChange("packageModifiers", key, e.target.value)}
                  />
                </div>
              ))}
            </div>
          </div>

          {/* CARD 10: VEHICLE MODIFIERS */}
          <div className="delivery-card" style={{ background: "var(--card-bg, #1a2234)", border: "1px solid var(--border-color, #2d3748)", borderRadius: "12px", padding: "20px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "16px" }}>
              <span style={{ fontSize: "24px" }}>🛵</span>
              <div>
                <h3 style={{ margin: 0, fontSize: "16px" }}>Vehicle Type Modifiers</h3>
                <p className="hint" style={{ margin: 0 }}>Bike is the 1.0 baseline; bicycle discount or van/truck surcharges.</p>
              </div>
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: "16px" }}>
              {Object.keys(VEHICLE_LABELS).map((key) => (
                <div key={key} className="form-field" style={{ margin: 0 }}>
                  <label style={{ fontSize: "12px", color: "var(--text-muted)" }}>{VEHICLE_LABELS[key]}</label>
                  <input
                    type="number"
                    step="0.05"
                    value={form.vehicleModifiers?.[key] ?? ""}
                    onChange={(e) => handleNestedChange("vehicleModifiers", key, e.target.value)}
                  />
                </div>
              ))}
            </div>
          </div>

          {/* CARD 11: RECEIVER ACKNOWLEDGEMENT FEES */}
          <div className="delivery-card" style={{ background: "var(--card-bg, #1a2234)", border: "1px solid var(--border-color, #2d3748)", borderRadius: "12px", padding: "20px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "16px" }}>
              <span style={{ fontSize: "24px" }}>✍️</span>
              <div>
                <h3 style={{ margin: 0, fontSize: "16px" }}>Receiver Acknowledgement (Proof of Delivery) Fees (₦)</h3>
                <p className="hint" style={{ margin: 0 }}>Added fee when sender requires specific proof of delivery verification.</p>
              </div>
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: "16px" }}>
              {Object.keys(RA_METHOD_LABELS).map((key) => (
                <div key={key} className="form-field" style={{ margin: 0 }}>
                  <label style={{ fontSize: "12px", color: "var(--text-muted)" }}>{RA_METHOD_LABELS[key]}</label>
                  <input
                    type="number"
                    value={form.raFees?.[key] ?? ""}
                    onChange={(e) => handleNestedChange("raFees", key, e.target.value)}
                  />
                </div>
              ))}
            </div>
          </div>

          {/* CARD 12: LAUNCHED CITIES */}
          <div className="delivery-card" style={{ background: "var(--card-bg, #1a2234)", border: "1px solid var(--border-color, #2d3748)", borderRadius: "12px", padding: "20px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "16px" }}>
              <span style={{ fontSize: "24px" }}>🌆</span>
              <div>
                <h3 style={{ margin: 0, fontSize: "16px" }}>Launched Operating Cities</h3>
                <p className="hint" style={{ margin: 0 }}>Cities where order dispatch and pricing are active.</p>
              </div>
            </div>

            <div style={{ display: "flex", flexWrap: "wrap", gap: "8px", marginBottom: "16px" }}>
              {(form.launchedCities || []).map((city) => (
                <span
                  key={city}
                  className="status-badge info"
                  style={{ display: "inline-flex", alignItems: "center", gap: "8px", padding: "6px 12px", fontSize: "13px" }}
                >
                  📍 {city}
                  <button
                    type="button"
                    onClick={() => handleRemoveCity(city)}
                    style={{ background: "none", border: "none", color: "inherit", cursor: "pointer", fontWeight: 700, padding: 0 }}
                  >✕</button>
                </span>
              ))}
            </div>

            <div style={{ display: "flex", gap: "8px", maxWidth: "360px" }}>
              <input
                type="text"
                placeholder="Add a new city, e.g. Ibadan"
                value={newCity}
                onChange={(e) => setNewCity(e.target.value)}
                style={{ margin: 0 }}
              />
              <button
                type="button"
                className="secondary"
                style={{ width: "auto", padding: "8px 16px", margin: 0 }}
                onClick={handleAddCity}
              >
                + Add City
              </button>
            </div>
          </div>

          {/* BOTTOM SAVE BUTTON */}
          <div style={{ display: "flex", justifyContent: "flex-end", marginTop: "8px" }}>
            <button
              disabled={saving}
              onClick={handleSave}
              style={{ width: "auto", minWidth: "200px", padding: "12px 28px", fontSize: "15px", fontWeight: 700 }}
            >
              {saving ? "Saving Changes..." : "💾 Save All Pricing Settings"}
            </button>
          </div>

          {/* AUDIT HISTORY LOG */}
          <div className="delivery-card" style={{ background: "var(--card-bg, #1a2234)", border: "1px solid var(--border-color, #2d3748)", borderRadius: "12px", padding: "20px" }}>
            <h3 style={{ margin: "0 0 12px" }}>📜 Change Audit Log</h3>
            {history.length === 0 ? (
              <p className="hint">No pricing changes recorded yet.</p>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: "8px", maxHeight: "360px", overflowY: "auto" }}>
                {history.map((entry) => (
                  <div key={entry._id} className="history-row" style={{ padding: "8px 12px", background: "rgba(255,255,255,0.02)", borderRadius: "6px" }}>
                    <div>
                      <strong>{entry.field}</strong>:{" "}
                      <span style={{ color: "var(--danger, #ef4444)" }}>{JSON.stringify(entry.oldValue)}</span>
                      {" → "}
                      <span style={{ color: "var(--success, #10b981)" }}>{JSON.stringify(entry.newValue)}</span>
                    </div>
                    <div className="hint" style={{ fontSize: "11px", marginTop: "2px" }}>
                      Changed by {entry.changedBy?.name || "Admin"} · {new Date(entry.changedAt).toLocaleString()}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

        </div>
      </div>
    </div>
  );
}
